import { animate, state, style, transition, trigger } from '@angular/animations';
import { ChangeDetectionStrategy, Component, computed, effect, signal, OnInit, inject } from '@angular/core';
import { TranslocoPipe, TranslocoService, provideTranslocoScope } from '@jsverse/transloco';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatError, MatFormField, MatLabel } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatProgressBar } from '@angular/material/progress-bar';
import { Router } from '@angular/router';
import { AddNewCompanyAutocompleteComponent } from '@shared/components/add-new-company-dialog/add-new-company-autocomplete/add-new-company-autocomplete.component';
import { AddNewCompanyAddressAutocompleteComponent } from './add-new-company-address-autocomplete/add-new-company-address-autocomplete.component';
import { MatCheckbox, MatCheckboxChange } from '@angular/material/checkbox';
import { MatButton } from '@angular/material/button';
import { catchError, combineLatest, debounceTime, EMPTY, of, switchMap, tap } from 'rxjs';
import { AddCompanyService } from '../../../add-entity/services/add-company.service';
import { cityTypeaheadEndpoint, filterNullishValues, getAddressValidationErrors, addressValidationErrorsToFormErrors, isFuzzyMatch, matchAddresses } from './add-new-company-dialog.utils';
import { CompanyPostModel, OfficePostModel } from '../../../add-entity/models/add-company.model';
import { MdmCompanyEnsureService } from '../../services/mdm-company-ensure.service';
import { ExistingCompanyWarningComponent } from './existing-company-warning/existing-company-warning.component';
import { AddressEditModel } from '@shared/models/address-edit.model';
import { AddAddressService } from '../../../add-entity/services/add-address.service';
import { TypeaheadComponent } from '../typeahead/typeahead.component';
import { TypeaheadOption } from '@shared/models/typeahead.model';
import { TypeaheadOptionType } from '@shared/models/typeahead.enum';
import { MatOption, MatSelect } from '@angular/material/select';
import { COUNTRY_OPTIONS, STATE_OPTIONS, vldtr } from '@shared/index';
import { SpaceType } from '@shared/models/space-type.enum';
import { AddressType } from '@shared/models/address-type.enum';
import { SnackbarService } from '@shared/info-snackbar/snackbar.service';
import { ProgressSpinnerDialogService } from '@shared/services/progress-spinner-dialog.service';
import { AmplitudeService } from '../../../core/services/amplitude.service';
import { AnalyticsEvent } from '../../../core/analytics/analytics-events';


export const slideOutUp = trigger('slideOutUp', [
  state('in', style({ transform: 'translateY(4px)', opacity: 0 })),
  state('out', style({ transform: 'translateY(-4px)', opacity: 1 })),
  transition('in => out', [
    animate('300ms ease-in')
  ]),
  transition('out => in', [
    animate('50ms ease-out')
  ]),
]);

const formSwitchAnimation = [
  trigger('contentSlide', [
    state('void', style({
      opacity: 0,
      transform: 'translateX(50px)',
      overflow: 'hidden'
    })),
    state('*', style({
      opacity: 1,
      transform: 'translateX(0)',
      overflow: 'visible',
    })),
    transition('void => *', [
      animate('300ms 150ms cubic-bezier(0.25, 0.8, 0.25, 1)')
    ]),
    transition('* => void', [
      animate('200ms cubic-bezier(0.25, 0.8, 0.25, 1)')
    ])
  ]),
]

@Component({
  selector: 'add-new-company-dialog',
  imports: [
    ReactiveFormsModule,
    MatFormField,
    MatProgressBar,
    MatInput,
    MatButton,
    MatSelect,
    MatOption,
    MatDialogModule,
    MatIcon,
    MatLabel,
    MatCheckbox,
    MatError,
    AddNewCompanyAddressAutocompleteComponent,
    ExistingCompanyWarningComponent,
    TypeaheadComponent,
    AddNewCompanyAutocompleteComponent,
    TranslocoPipe],
  providers: [provideTranslocoScope('sharedDialogs')],
  templateUrl: './add-new-company-dialog.component.html',
  styleUrl: './add-new-company-dialog.component.scss',
  animations: [slideOutUp, formSwitchAnimation],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddNewCompanyDialogComponent implements OnInit {
  public companyIsSelected = signal(false);
  public manualEntryName = signal<string | null>(null);
  public alreadyExistingCompanyAddressMatch = signal<CompanyPostModel | null>(null);
  public animationState = computed<'in' | 'out'>(() => this.companySearchInProgress() || this.addressSearchInProgress() ? 'out' : 'in');
  public companySearchInProgress = signal(false);
  public addressSearchInProgress = signal(false);

  public companyName = new FormControl<string | null>(null);

  public newAddressForm = new FormGroup({
    line2: new FormControl<string | null>(null),
    city: new FormControl<string | null>(null, Validators.required),
    stateProvince: new FormControl<string | null>(null, Validators.required),
    postalCode: new FormControl<string | null>(null, Validators.required),
    country: new FormControl<string | null>('US', [Validators.required]),
  });

  public manualEntryForm = new FormGroup({
    name: new FormControl<string | null>(null, Validators.required),
    address: new FormControl<AddressEditModel | null>(null, Validators.required),
    newAddress: this.newAddressForm,
    website: new FormControl<string | null>(null, [
      Validators.required,
      vldtr.website
    ]),
  });

  private readonly transloco = inject(TranslocoService);
  public cityTypeaheadEndpoint = cityTypeaheadEndpoint;
  public cityOptions = signal<TypeaheadOption | TypeaheadOption[]>([]);
  public countryOptions = COUNTRY_OPTIONS;
  public stateOptions = STATE_OPTIONS;

  constructor(
    private dialogRef: MatDialogRef<AddNewCompanyDialogComponent>,
    private addCompanyService: AddCompanyService,
    private addressService: AddAddressService,
    private notificationService: SnackbarService,
    private spinnerService: ProgressSpinnerDialogService,
    private mdmCompanyEnsure: MdmCompanyEnsureService,
    private amplitude: AmplitudeService,
    private router: Router) { }

  _ = effect(() => {
    const value = this.manualEntryName();
    const cityOptions = this.cityOptions()

    if (value) {
      this.manualEntryForm.patchValue({ name: value });
    }

    if (cityOptions && !Array.isArray(cityOptions)) {
      this.newAddressForm.get('city')?.setValue(cityOptions.value, { emitEvent: false });
    }
  })

  get isZipRequired(): boolean {
    return !!this.newAddressForm.get('postalCode')?.hasValidator(Validators.required)
  }

  get isStateRequired(): boolean {
    return !!this.newAddressForm.get('stateProvince')?.hasValidator(Validators.required)
  }

  get isSaveDisabled(): boolean {
    if (this.alreadyExistingCompanyAddressMatch()) {
      return true;
    }

    if (this.manualEntryForm.get('name')?.invalid) {
      return true;
    }

    if (this.manualEntryForm.get('address')?.invalid) {
      return true;
    }

    if (this.manualEntryForm.get('website')?.invalid) {
      return true;
    }

    if (this.newAddressForm.enabled && this.newAddressForm.invalid) {
      return true;
    }

    return false;
  }

  ngOnInit() {
    this.newAddressForm.disable();
    combineLatest([
      this.manualEntryForm.get('name')!.valueChanges,
      this.manualEntryForm.get('address')!.valueChanges.pipe(
        tap(() => this.newAddressForm.disable()))
    ]).pipe(
      debounceTime(300),
      tap(() => {
        this.alreadyExistingCompanyAddressMatch.set(null);
        this.addressSearchInProgress.set(true);
      }),
      switchMap(([name, address]) => {
        if (name && address) {
          return this.addCompanyService.searchCompany(name).pipe(
            catchError(() => of([]))
          );
        }
        return of([]);
      })
    ).subscribe(companies => {
      if (companies.length > 0) {
        for (const company of companies) {

          if (isFuzzyMatch(company.name, this.manualEntryForm.get('name')?.value!)) {
            if (company.offices[0].address) {
              if (matchAddresses(company.offices[0].address, this.manualEntryForm.get('address')?.value!) > 0.85) {
                this.alreadyExistingCompanyAddressMatch.set(company);
                break;
              }
            }
          }
        }
      }
      this.addressSearchInProgress.set(false);
    })

    this.newAddressForm.get('country')?.valueChanges.subscribe((country) => {

      this.newAddressForm.get('stateProvince')?.reset();
      this.newAddressForm.get('city')?.reset();
      if (country === 'US') {
        this.newAddressForm.get('stateProvince')?.setValidators([Validators.required]);
      } else {
        this.newAddressForm.get('stateProvince')?.clearValidators();
      }

      if (country === 'UK') {
        this.newAddressForm.get('postalCode')?.clearValidators();
      } else {
        this.newAddressForm.get('postalCode')?.setValidators([Validators.required]);
      }
      this.newAddressForm.get('stateProvince')?.updateValueAndValidity();
      this.newAddressForm.get('postalCode')?.updateValueAndValidity();
    })
  }

  public onStateEmitted(state: string): void {
    this.newAddressForm.get('stateProvince')?.setValue(state, { emitEvent: false });
  }

  public onFocusStateCity(event: any): void {
    this.newAddressForm.get('city')?.markAsDirty();
    this.newAddressForm.get('stateProvince')?.markAsDirty();
    event.stopPropagation();
  }

  toggleWebsiteControl(check: MatCheckboxChange['checked']) {
    if (check) {
      this.manualEntryForm.get('website')?.reset();
      this.manualEntryForm.get('website')?.disable();
    } else {
      this.manualEntryForm.get('website')?.enable();
    }
  }

  enableNewAddressForm() {
    this.newAddressForm.enable();
    const address = this.manualEntryForm.get('address')?.value;
    const country = address?.country || 'US';
    this.newAddressForm.patchValue({
      line2: address?.line2 ?? null,
      city: address?.city ?? null,
      stateProvince: address?.stateProvince ?? null,
      postalCode: address?.postalCode ?? null,
      country,
    });

    if (address?.city) {
      const displayName = address.stateProvince
        ? `${address.city}, ${address.stateProvince}`
        : address.city;
      this.cityOptions.set({
        id: address.city,
        value: address.city,
        displayName,
        displayIcon: '',
        typeaheadResultType: TypeaheadOptionType.City,
      });
    }
  }

  setManualEntryName(name: string | null): void {
    this.manualEntryName.set(name);
    this.manualEntryForm.patchValue({ name }, { emitEvent: false });
  }

  closeDialog(): void {
    this.dialogRef.close();
  }

  saveCompany() {
    this.spinnerService.showProgressSpinner();
    const payload: Partial<CompanyPostModel> = {
      name: this.manualEntryForm.get('name')?.value!,
      website: this.manualEntryForm.get('website')?.value!,
      contacts: 0,
      offices: [{
        officeName: this.manualEntryForm.get('address')?.value?.city ?? '',
        address: { ...this.manualEntryForm.get('address')?.value!, verified: true },
        mainOffice: true,
      }],
    }
    let addressToSave: AddressEditModel = this.manualEntryForm.get('address')?.value!;
    console.log(addressToSave);
    
    if (this.newAddressForm.enabled) {
      addressToSave = {
        ...this.newAddressForm.value,
        addressType: AddressType.Work,
        addressLinked: false,
        id: 0,
        spaceId: 0,
        spaceType: SpaceType.Work,
        latitude: null,
        longitude: null,
        verified: false,
        streetAddress: this.manualEntryForm.get('address')?.value?.streetAddress || '',
        fullAddress: filterNullishValues({
          streetAddress: addressToSave.streetAddress,
          city: this.newAddressForm.get('city')?.value!,
          stateProvince: this.newAddressForm.get('stateProvince')?.value!,
          postalCode: this.newAddressForm.get('postalCode')?.value!,
        } as AddressEditModel),
      } as AddressEditModel;

    }

    const addressValidationErrors = getAddressValidationErrors(addressToSave);
    if (addressValidationErrors.length > 0) {
      this.spinnerService.hideProgressSpinner();
      this.manualEntryForm.get('address')?.setErrors(
        addressValidationErrorsToFormErrors(addressValidationErrors)
      );
      this.manualEntryForm.get('address')?.markAsTouched();
      return;
    }

    this.manualEntryForm.markAllAsTouched();
    this.addressService.saveAddress(addressToSave)
      .pipe(
          catchError(err => {
          this.spinnerService.hideProgressSpinner();
          if(err.error.vm) {
            this.notificationService.showError(
              this.transloco.translate('sharedDialogs.addNewCompany.messages.addressValidationError', { detail: err.error.vm }),
              10000,
            );
            return EMPTY;
          }
          this.notificationService.showError(
            this.transloco.translate('sharedDialogs.addNewCompany.messages.addressValidationRetry'),
            10000,
          );
          console.error(err.error);
          return EMPTY;
        }),
        switchMap(address => {
          return this.addCompanyService.addCompany({
            ...payload,
            offices: [{
              officeName: address.city,
              address,
              mainOffice: true,
            }],
          }).pipe(
            catchError(err => {
              this.spinnerService.hideProgressSpinner();
              this.notificationService.showError(
                this.transloco.translate('sharedDialogs.addNewCompany.messages.saveError'),
                10000,
              );
              return EMPTY;
            })
          )
        }),
      ).subscribe(async (result) => {
        const companyId = result?.company?.id;
        if (!companyId) {
          this.spinnerService.hideProgressSpinner();
          this.notificationService.showError(
            this.transloco.translate('sharedDialogs.addNewCompany.messages.navigationFailed'),
            10000,
          );
          return;
        }

        this.amplitude.track(AnalyticsEvent.CompanyCreated, {
          id: result.company.id,
          source: 'dialog',
        });

        await this.router.navigateByUrl('/', { skipLocationChange: true });
        await this.router.navigate(['/companies/company', companyId]);
        this.spinnerService.hideProgressSpinner();
        this.closeDialog();
        this.showNotification(result.company.name);
      });
  }

  showNotification(companyName: string): void {
    this.notificationService.showSuccess(
      this.transloco.translate('sharedDialogs.addNewCompany.messages.companyReady', { name: companyName }),
      10000,
      this.transloco.translate('sharedDialogs.addNewCompany.messages.newCompanyAddedTitle'),
    );
  }

  reset() {
    this.manualEntryName.set(null);
    this.alreadyExistingCompanyAddressMatch.set(null);
    this.newAddressForm.reset();
    this.manualEntryForm.get('website')?.enable();
    this.newAddressForm.disable();
  }

  /**
   * MDM-only autocomplete selection: save office address → create Ideal company
   * (with MdmUri/Nck for sync) → set model id → navigate to company overview.
   */
  createFromMdmCompany(company: CompanyPostModel): void {
    if (!company) {
      return;
    }

    const offices = company.offices ?? [];
    const hasAddress = offices.some(o => !!o.address?.streetAddress || !!o.address?.city);
    if (!company.mdmUri && !hasAddress) {
      this.notificationService.showError(
        'Selected company is missing address details. Use Add Company to enter them manually.',
        10000,
      );
      return;
    }

    this.spinnerService.showProgressSpinner();
    this.mdmCompanyEnsure.ensureIdealCompanyId({
      key: 0,
      display: company.name,
      value: company.name,
      website: company.website,
      isMdmMatch: true,
      mdmUri: company.mdmUri,
      nck: company.nck,
      mdmOffices: offices.map(office => {
        const wizardOffice = office as OfficePostModel & { mdmId?: string; mdmEnetId?: string };
        return {
          streetAddress: office.address?.streetAddress,
          line2: office.address?.line2,
          city: office.address?.city,
          stateProvince: office.address?.stateProvince,
          postalCode: office.address?.postalCode,
          country: office.address?.country,
          address: office.address?.fullAddress,
          isHq: office.mainOffice || !!(office as { isPrimary?: boolean }).isPrimary,
          mdmUri: office.mdmUri || wizardOffice.mdmId || company.mdmUri,
          nck: office.nck || company.nck,
          enetId: office.enetId || wizardOffice.mdmEnetId,
        };
      }),
    }).pipe(
      catchError(() => {
        this.spinnerService.hideProgressSpinner();
        this.notificationService.showError(
          'An error occurred while saving the company. Please try again later.',
          10000,
        );
        return EMPTY;
      }),
    ).subscribe(async (companyId) => {
      this.spinnerService.hideProgressSpinner();
      if (!companyId) {
        this.notificationService.showError(
          'Unable to create company from MDM selection.',
          10000,
        );
        return;
      }
      company.id = companyId;
      company.isMdmMatch = false;
      await this.navigateToCompany(companyId);
    });
  }

  async navigateToCompany(id: number): Promise<void> {
    this.spinnerService.showProgressSpinner();
    await this.router.navigateByUrl('/', { skipLocationChange: true })
    await this.router.navigate(['/companies/company', id]).then(() => {
      this.spinnerService.hideProgressSpinner();
      this.closeDialog();
    });
  }

}